// ============================================================================
// 主题工具模块（纯 JS，不依赖 Vue）
// 负责：主题模式（light / dark / system）、主题颜色、持久化 & 应用
// ============================================================================

export const THEME_KEY = 'app-theme-mode' // 存储值: 'light' | 'dark' | 'system'
export const THEME_COLOR_KEY = 'app-theme-color'

const VALID_MODES = ['light', 'dark', 'system']

// ---------------------------------------------------------------------------
// 辅助：读取 / 写入 localStorage（带安全兜底）
// ---------------------------------------------------------------------------
function safeGet(key, fallback) {
  try {
    const v = localStorage.getItem(key)
    return v == null ? fallback : v
  } catch (e) {
    return fallback
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    /* 忽略 */
  }
}

// ---------------------------------------------------------------------------
// 辅助：hex → YIQ 亮度，用于计算导航栏文字颜色
// ---------------------------------------------------------------------------
function getNavTextColor(themeColor) {
  if (!themeColor) return '#ffffff'
  const hex = String(themeColor).replace('#', '')
  const fullHex =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex
  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) return '#ffffff'
  const r = parseInt(fullHex.substr(0, 2), 16)
  const g = parseInt(fullHex.substr(2, 2), 16)
  const b = parseInt(fullHex.substr(4, 2), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness >= 160 ? '#222222' : '#ffffff'
}

// ---------------------------------------------------------------------------
// 获取系统偏好的主题
// ---------------------------------------------------------------------------
export function getSystemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

// ---------------------------------------------------------------------------
// 获取当前已解析生效的模式（将 'system' 解析为 light/dark）
// ---------------------------------------------------------------------------
export function getEffectiveMode() {
  const mode = getThemeMode()
  if (mode === 'system') return getSystemPrefersDark() ? 'dark' : 'light'
  return mode === 'dark' ? 'dark' : 'light'
}

// ---------------------------------------------------------------------------
// 获取用户显式设置的主题模式
// ---------------------------------------------------------------------------
export function getThemeMode() {
  const raw = safeGet(THEME_KEY, 'light')
  return VALID_MODES.indexOf(raw) >= 0 ? raw : 'light'
}

// ---------------------------------------------------------------------------
// 获取主题颜色
// ---------------------------------------------------------------------------
export function getThemeColor() {
  return safeGet(THEME_COLOR_KEY, '#F56C6C')
}

// ---------------------------------------------------------------------------
// 实际应用主题：设置 document.documentElement[data-theme]、
// body 的背景色变量，并给 html/body 加上平滑过渡
// 注意：主题切换时需要保留用户的壁纸/纯色背景设置
// ---------------------------------------------------------------------------
export function applyTheme(mode) {
  const effective =
    (mode === 'system' && getSystemPrefersDark()) || mode === 'dark'
      ? 'dark'
      : 'light'

  const html = document.documentElement
  const body = document.body

  // 给 html / body 加过渡（只加一次，避免重复叠加）
  if (html && !html.dataset.themeTransitionAdded) {
    html.style.transition =
      (html.style.transition ? html.style.transition + ', ' : '') +
      'background-color 0.3s ease, color 0.3s ease'
    html.dataset.themeTransitionAdded = '1'
  }
  if (body) {
    body.style.transition =
      (body.style.transition ? body.style.transition + ', ' : '') +
      'background-color 0.3s ease, color 0.3s ease'
  }

  html.setAttribute('data-theme', effective)

  if (body) {
    body.style.color = 'var(--text-color)'

    // 读取用户的壁纸设置
    const wallpaperType = (() => {
      try { return localStorage.getItem('wallpaperType') || 'default' } catch (e) { return 'default' }
    })()
    const wallpaperUrl = (() => {
      try { return localStorage.getItem('wallpaperUrl') || '' } catch (e) { return '' }
    })()
    const wallpaperColor = (() => {
      try { return localStorage.getItem('wallpaperColor') || '' } catch (e) { return '' }
    })()

    // 根据壁纸类型应用设置
    if ((wallpaperType === 'preset' || wallpaperType === 'custom') && wallpaperUrl) {
      body.style.backgroundImage = `url("${wallpaperUrl}")`
      body.style.backgroundColor = ''
      body.classList.add('has-wallpaper')
    } else if (wallpaperType === 'color' && wallpaperColor) {
      body.style.backgroundImage = ''
      body.style.backgroundColor = wallpaperColor
      body.classList.remove('has-wallpaper')
    } else {
      // default：使用主题的 CSS 变量作为背景色
      body.style.backgroundImage = ''
      body.style.backgroundColor = 'var(--background-color)'
      body.classList.remove('has-wallpaper')
    }
  }
}

// ---------------------------------------------------------------------------
// 应用主题颜色：设置 --theme-color / --theme-color-light / --nav-background
// 以及导航栏文字颜色（YIQ 亮度计算）
// ---------------------------------------------------------------------------
export function applyThemeColor(color) {
  if (!color) return
  const html = document.documentElement
  const navTextColor = getNavTextColor(color)
  const isDarkText = navTextColor === '#222222'

  html.style.setProperty('--theme-color', color)
  html.style.setProperty('--theme-color-light', color + '1A')
  html.style.setProperty('--nav-background', color)
  html.style.setProperty('--nav-text-color', navTextColor)
  html.style.setProperty(
    '--nav-icon-hover',
    isDarkText ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.18)'
  )
  html.style.setProperty(
    '--nav-icon-hover-active',
    isDarkText ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.32)'
  )
}

// ---------------------------------------------------------------------------
// 设置主题模式（持久化 + 应用）
// ---------------------------------------------------------------------------
export function setThemeMode(mode) {
  const normalized = VALID_MODES.indexOf(mode) >= 0 ? mode : 'light'
  safeSet(THEME_KEY, normalized)
  applyTheme(normalized)
  return normalized
}

// ---------------------------------------------------------------------------
// 设置主题颜色（持久化 + 应用）
// ---------------------------------------------------------------------------
export function setThemeColor(color) {
  if (!color) return getThemeColor()
  safeSet(THEME_COLOR_KEY, color)
  applyThemeColor(color)
  return color
}

// ---------------------------------------------------------------------------
// 切换亮/暗（在 light/dark 之间互切；若当前是 system，则先固定为当前生效模式的反义）
// 返回新的模式
// ---------------------------------------------------------------------------
export function toggleTheme() {
  const current = getThemeMode()
  let next
  if (current === 'light') {
    next = 'dark'
  } else if (current === 'dark') {
    next = 'light'
  } else {
    // system：取反当前系统偏好
    next = getSystemPrefersDark() ? 'light' : 'dark'
  }
  return setThemeMode(next)
}

// ---------------------------------------------------------------------------
// 初始化：在应用启动时调用，从 localStorage 读取主题 + 颜色并应用
// ---------------------------------------------------------------------------
export function initTheme() {
  const mode = getThemeMode()
  const color = getThemeColor()
  applyTheme(mode)
  applyThemeColor(color)

  // 跟随系统模式：监听系统偏好变化
  if (
    mode === 'system' &&
    typeof window !== 'undefined' &&
    window.matchMedia
  ) {
    try {
      const mql = window.matchMedia('(prefers-color-scheme: dark)')
      const onChange = () => applyTheme('system')
      if (mql.addEventListener) {
        mql.addEventListener('change', onChange)
      } else if (mql.addListener) {
        mql.addListener(onChange)
      }
    } catch (e) {
      /* 忽略 */
    }
  }
}

// ---------------------------------------------------------------------------
// 应用背景设置（壁纸类型、壁纸URL、纯色背景）
// 用于登录后从后端获取数据并统一应用
// ---------------------------------------------------------------------------
export function applyBackgroundSetting(type, url, color) {
  const body = document.body
  if (!body) return

  // 持久化到 localStorage（与 BackgroundSetting.vue 保持一致）
  safeSet('wallpaperType', type || 'default')
  safeSet('wallpaperUrl', url || '')
  safeSet('wallpaperColor', color || '')

  let hasWallpaper = false
  let targetBgImage = ''
  let targetBgColor = ''

  if ((type === 'preset' || type === 'custom') && url) {
    targetBgImage = `url("${url}")`
    hasWallpaper = true
  } else if (type === 'color' && color) {
    targetBgColor = color
    hasWallpaper = false
  } else {
    // default：使用主题的 CSS 变量作为背景色
    hasWallpaper = false
  }

  // 应用到 body
  body.style.backgroundImage = targetBgImage
  body.style.backgroundColor = targetBgColor || 'var(--background-color)'
  if (hasWallpaper) {
    body.classList.add('has-wallpaper')
  } else {
    body.classList.remove('has-wallpaper')
  }
}

// ---------------------------------------------------------------------------
// 从后端加载用户主题/背景设置并应用
// 返回 Promise，便于 App.vue 等调用
// ---------------------------------------------------------------------------
export async function loadUserThemeFromBackend($axios, userId) {
  if (!$axios || !userId) return null

  try {
    const res = await $axios.get('/api/user/theme/get', { params: { userId } })
    if (res && res.data && res.data.code === 200 && res.data.data) {
      const data = res.data.data

      // 应用主题模式
      if (data.themeMode && ['light', 'dark', 'system'].indexOf(data.themeMode) >= 0) {
        setThemeMode(data.themeMode)
      }

      // 应用主题颜色
      if (data.themeColor) {
        setThemeColor(data.themeColor)
      }

      // 应用背景设置
      // 后端 wallpaperType 只有 'default'/'custom'，需要结合 url/color 判断实际类型
      const backendType = data.wallpaperType || 'default'
      const url = data.wallpaperUrl || ''
      const color = data.wallpaperColor || ''

      // 将后端格式转为前端格式
      let actualType = backendType
      if (backendType === 'default') {
        if (url && url.trim()) actualType = 'preset'
        else if (color && color.trim()) actualType = 'color'
        else actualType = 'default'
      }

      applyBackgroundSetting(actualType, url, color)

      return data
    }
  } catch (e) {
    console.warn('从后端加载主题设置失败:', e)
  }

  return null
}
